/**
 * Open Flame Protection Requirement Evaluator
 * Evaluates whether vessel meets WBC3 16.3.1 open flame cooking protection requirements
 * 
 * Requirement: One 5A/34B AND (8A/68B OR fire blanket OR fixed system per 16.4)
 */

/**
 * Evaluate if vessel meets open flame protection requirements
 * @param {Object} vessel - Vessel configuration
 * @returns {Object} Evaluation result with status and details
 */
export function evaluateOpenFlame(vessel) {
  if (!vessel) {
    return {
      required: { baseExt: '5A/34B', oneOf: ['8A/68B', 'Blanket', 'Fixed system'] },
      selected: { baseExt: false, extraExt: false, blanket: false, fixed: false },
      status: 'missing',
      reasons: ['No vessel configuration provided'],
      fulfilledBy: []
    };
  }

  // Check what protection methods are selected
  const selected = {
    baseExt: checkHasBaseExtinguisher(vessel),
    extraExt: vessel.openFlameProtection?.extraExtinguisher || false,
    blanket: vessel.openFlameProtection?.blanket || false,
    fixed: vessel.openFlameProtection?.fixedSystem || false
  };

  // Build list of what's fulfilling the requirement
  const fulfilledBy = [];
  const reasons = [];

  // Check base requirement (5A/34B)
  if (selected.baseExt) {
    fulfilledBy.push('5A/34B');
  } else {
    reasons.push('Missing base 5A/34B extinguisher');
  }

  // Check "one of" requirement (8A/68B OR blanket OR fixed)
  const oneOfFulfilled = [];
  if (selected.extraExt) {
    oneOfFulfilled.push('8A/68B');
    fulfilledBy.push('Additional extinguisher (8A/68B)');
  }
  if (selected.blanket) {
    oneOfFulfilled.push('Blanket');
    fulfilledBy.push('Fire blanket');
  }
  if (selected.fixed) {
    oneOfFulfilled.push('Fixed system');
    fulfilledBy.push('Fixed system');
  }

  const hasOneOf = oneOfFulfilled.length > 0;
  if (!hasOneOf) {
    reasons.push('Neither 8A/68B, Blanket, nor Fixed system selected');
  }

  // Determine overall status
  let status;
  if (selected.baseExt && hasOneOf) {
    status = 'meets';
    reasons.length = 0; // Clear reasons if fully meeting
    reasons.push('All requirements satisfied');
  } else if (selected.baseExt || hasOneOf) {
    status = 'partial';
  } else {
    status = 'missing';
  }

  return {
    required: { 
      baseExt: '5A/34B', 
      oneOf: ['8A/68B', 'Blanket', 'Fixed system'] 
    },
    selected,
    status,
    reasons,
    fulfilledBy
  };
}

/**
 * Check if vessel has the base 5A/34B extinguisher
 * This checks the canonical equipment requirements to see if a 5A/34B is present
 * 
 * @param {Object} vessel - Vessel configuration
 * @returns {boolean} True if 5A/34B extinguisher is present
 */
function checkHasBaseExtinguisher(vessel) {
  // The base 5A/34B extinguisher is a standard requirement for any vessel with cooking/galley
  // It's part of the basic FFE requirements, not an optional selection
  
  // If vessel has any cooking facilities, the base extinguisher is required by regulations
  // and we assume it's present (since it's mandatory, not optional)
  if (vessel.hasGalley || vessel.hasCooking) {
    // The base 5A/34B is a regulatory requirement that should already be installed
    // This is different from the ADDITIONAL 8A/68B which is one of the open-flame options
    return true;
  }
  
  // TODO: In the future, this could check actual equipment inventory
  // to verify the 5A/34B extinguisher is actually present
  
  return false;
}

/**
 * Get display status with appropriate icon
 * @param {string} status - Status from evaluation
 * @returns {Object} Display object with text and color
 */
export function getStatusDisplay(status) {
  switch (status) {
    case 'meets':
      return { text: 'Meets requirement ✅', color: 'green', icon: '✅' };
    case 'partial':
      return { text: 'Partial compliance ⚠️', color: 'yellow', icon: '⚠️' };
    case 'missing':
      return { text: 'Not compliant ❌', color: 'red', icon: '❌' };
    default:
      return { text: 'Unknown', color: 'gray', icon: '?' };
  }
}

/**
 * Format fulfillment display for UI
 * @param {string[]} fulfilledBy - Array of fulfillment items
 * @returns {string} Formatted string for display
 */
export function formatFulfillment(fulfilledBy) {
  if (!fulfilledBy || fulfilledBy.length === 0) {
    return 'No protection methods selected';
  }
  return `Fulfilled by: ${fulfilledBy.join(', ')}`;
}